import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Search, Phone, Mail, Inbox, Loader2, Building2, CalendarDays,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLogin from "@/components/admin/AdminLogin";
import { Input } from "@/components/ui/input";

const urgencyStyle = (u) => {
  if (u.includes("Acil")) return "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/25";
  if (u.includes("Normal")) return "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/25";
  return "bg-white/5 text-[#9CA3AF] border-white/10";
};

const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center font-code text-[11px] border rounded-full px-3 py-1 ${className}`}>
    {children}
  </span>
);

const Dashboard = () => {
  const { setUser } = useAuth();
  const [apps, setApps] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/applications")
      .then((r) => setApps(r.data))
      .catch(() => toast.error("Başvurular yüklenemedi"));
  }, []);

  const filtered = useMemo(() => {
    if (!apps) return [];
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) =>
      [a.full_name, a.business_name, a.email, a.project_description, a.service_type]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [apps, query]);

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div data-testid="admin-dashboard-container" className="min-h-screen bg-[#08070D] text-white">
      <div className="grain" />
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#08070D]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-logo" className="font-display font-extrabold text-xl tracking-tight">
              BEFORA<span className="text-[#10B981]">.</span>
            </Link>
            <span className="font-code text-xs text-[#6B7280] border border-white/10 rounded-full px-3 py-1">
              Yönetici Paneli
            </span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-red-400 border border-white/10 hover:border-red-400/40 rounded-full px-4 py-2 transition-colors duration-300"
          >
            <LogOut size={14} />
            Çıkış
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Proje Başvuruları</h1>
            <p className="text-sm text-[#6B7280] mt-2">
              {apps ? `${filtered.length} başvuru listeleniyor` : "Yükleniyor..."}
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input
              data-testid="admin-search-input"
              placeholder="İsim, işletme veya açıklamada ara..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-11 bg-[#151421] border-white/10 focus:border-[#10B981]/50 text-white h-11 rounded-full"
            />
          </div>
        </div>

        {apps === null ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 size={28} className="animate-spin text-[#10B981]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <Inbox size={48} className="text-[#374151]" />
            <p className="text-[#6B7280] mt-6">
              {apps.length === 0 ? "Henüz başvuru yok." : "Aramayla eşleşen başvuru bulunamadı."}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {filtered.map((a, i) => (
              <motion.article
                key={a.id}
                data-testid="admin-application-card"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.4) }}
                className="glow-card bg-[#151421] border border-white/[0.08] rounded-2xl p-7"
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-display font-bold text-xl tracking-tight">{a.full_name}</h2>
                      <Badge className={urgencyStyle(a.urgency)}>{a.urgency}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-sm text-[#9CA3AF]">
                      <span className="flex items-center gap-1.5">
                        <Building2 size={13} className="text-[#6B7280]" />
                        {a.business_name}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <CalendarDays size={13} className="text-[#6B7280]" />
                        {new Date(a.created_at).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Badge className="bg-[#6366F1]/10 text-[#818CF8] border-[#6366F1]/25">{a.service_type}</Badge>
                      <Badge className="bg-white/5 text-[#D1D5DB] border-white/10">{a.budget_range}</Badge>
                    </div>
                    <p className="text-sm text-[#9CA3AF] leading-relaxed mt-4 whitespace-pre-wrap">
                      {a.project_description}
                    </p>
                  </div>

                  <div className="flex lg:flex-col gap-3 shrink-0">
                    <a
                      href={`tel:${a.phone.replace(/\s/g, "")}`}
                      data-testid={`admin-call-button-${a.id}`}
                      className="flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#0ea371] text-[#08070D] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
                    >
                      <Phone size={14} />
                      {a.phone}
                    </a>
                    <a
                      href={`mailto:${a.email}`}
                      data-testid={`admin-email-button-${a.id}`}
                      className="flex items-center justify-center gap-2 border border-white/15 hover:border-[#10B981]/50 text-sm text-white px-5 py-2.5 rounded-full transition-colors duration-300"
                    >
                      <Mail size={14} />
                      E-posta Gönder
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#08070D] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#10B981]" />
      </div>
    );
  }
  if (!user) return <AdminLogin />;
  return <Dashboard />;
}
